import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { BULK_DELETE_LIMIT } from '~/common/constants/max_lengths';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { DocumentsCountPayload } from '~/redux/api/methods/documents';
import { ApiError } from '~/types/ApiError';
import { ERROR } from '~/types/toast.types';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';

import LimitExceededModal from '../LimitExceededModal/LimitExceededModal';

const CHAR_LIMIT = 100;

interface DeleteDocumentWithReasonModalProps {
  type: string;
  isVisible: boolean;
  count: number;
  selectedItemName?: string;
  fetchDocumentsCount?: () => Promise<DocumentsCountPayload>;
  hideModal: () => void;
  onConfirmDelete: (...args: any[]) => void;
}

const DeleteDocumentWithReasonModal = ({
  type,
  isVisible,
  count,
  fetchDocumentsCount,
  hideModal,
  onConfirmDelete,
}: DeleteDocumentWithReasonModalProps) => {
  const intl = useIntl();
  const options = [
    {
      label: intl.formatMessage({
        id: 'DeleteDocumentWithReasonModal.duplicateOption',
        defaultMessage: 'Duplicate',
        description: 'Option for duplicate reason',
      }),
      value: 'duplicate',
      selected: false,
    },
    {
      label: intl.formatMessage({
        id: 'DeleteDocumentWithReasonModal.mistakeOption',
        defaultMessage: 'Added by mistake',
        description: 'Option for added by mistake reason',
      }),
      value: 'mistake',
      selected: false,
    },
    {
      label: intl.formatMessage({
        id: 'DeleteDocumentWithReasonModal.otherOption',
        defaultMessage: 'Other',
        description: 'Option for other reason',
      }),
      value: 'other',
      selected: false,
    },
  ];

  const [deleteReason, setDeleteReason] = useState(null);
  const [deleteComment, setDeleteComment] = useState('');
  const [documentCount, setDocumentCount] = useState(count);
  const [overLimit, setOverLimit] = useState(
    (documentCount ?? count) > BULK_DELETE_LIMIT,
  );
  const [isLoaded, setIsLoaded] = useState(
    !fetchDocumentsCount || count > BULK_DELETE_LIMIT,
  );

  const onFetchDocumentCountError = (error: ApiError) => {
    if (error?.response?.status === HttpStatusCodeType.Forbidden) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DeleteDocumentWithReasonModal.forbiddenError',
          defaultMessage:
            'Sorry, the action cannot be performed because you do not have edit access to one of the selected folders.',
          description: 'Error message when user lacks edit access to folders',
        }),
      );
      handleHide();
    } else if (!overLimit) {
      setIsLoaded(true);
    } else {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DeleteDocumentWithReasonModal.genericError',
          defaultMessage: 'An error occurred. Please try again.',
          description: 'Generic error message for document count fetch',
        }),
      );
      handleHide();
    }
  };

  useEffect(() => {
    const fetchCount = async () => {
      let document_count = 0;
      if (!!fetchDocumentsCount) {
        ({ document_count } = await fetchDocumentsCount());
      }
      if (document_count) {
        setDocumentCount(document_count);
        setOverLimit(document_count > BULK_DELETE_LIMIT);
      }
      setIsLoaded(true);
    };
    if (!isLoaded) {
      fetchCount().catch(onFetchDocumentCountError);
    }
  }, [count]);

  const handleCommentChange = (comment: string | null): void => {
    if (!comment) setDeleteComment('');
    if (comment && comment?.length <= CHAR_LIMIT) {
      setDeleteComment(comment.slice(0, CHAR_LIMIT));
    }
  };

  const handleHide = () => {
    setDocumentCount(0);
    setOverLimit(false);
    setIsLoaded(false);
    setDeleteReason(null);
    setDeleteComment('');
    hideModal();
  };

  const handleOnConfirm = () => {
    onConfirmDelete(deleteReason, deleteComment.trim());
    handleHide();
  };

  // 1. must choose a reason, 2. if reason is 'other', must provide details
  const deleteDisabled =
    !deleteReason || (deleteReason === 'other' && !deleteComment.trim());

  let content = (
    <Modal
      title={intl.formatMessage(
        {
          id: 'DeleteDocumentWithReasonModal.title',
          defaultMessage:
            'Delete {count} {count, plural, one {item} other {items}}?',
          description: 'Title for delete confirmation modal',
        },
        { count },
      )}
      primaryAction={{
        variant: 'danger',
        icon: 'trash',
        onClick: handleOnConfirm,
        text: intl.formatMessage({
          id: 'DeleteDocumentWithReasonModal.deleteButton',
          defaultMessage: 'Delete',
          description: 'Text for delete button',
        }),
        disabled: deleteDisabled,
      }}
      isVisible={isVisible}
      onHide={handleHide}
      onCancel={handleHide}
    >
      <Layout direction="column" spacing={4}>
        <Text styles={{ 'overflow-wrap': 'break-word' }}>
          {intl.formatMessage(
            {
              id: 'DeleteDocumentWithReasonModal.confirmationText',
              defaultMessage:
                'The selected {count, plural, one {item} other {items}}  will be removed from your document page. Are you sure you want to remove the {count, plural, one {item} other {items}} {contentsText}?',
              description:
                'Confirmation text for deletion with conditional contents removal',
            },
            {
              count,
              contentsText:
                type !== 'document'
                  ? intl.formatMessage(
                      {
                        id: 'DeleteDocumentWithReasonModal.contentsText',
                        defaultMessage:
                          'and all of {count, plural, one {its} other {their}} contents',
                        description: 'Additional text for removing contents',
                      },
                      { count },
                    )
                  : '',
            },
          )}
        </Text>
        <Layout direction="column" spacing={2}>
          <Text variant="body-medium">
            {intl.formatMessage(
              {
                id: 'DeleteDocumentWithReasonModal.reasonPromptDocument',
                defaultMessage:
                  'Please provide a reason to delete the selected {count, plural, one {item} other {items}}',
                description: 'Prompt for reason to remove document items',
              },
              {
                count,
              },
            )}
          </Text>
          <RadioGroup
            options={options}
            value={deleteReason}
            onChange={setDeleteReason}
          />
        </Layout>
        <div>
          <Layout direction="column" spacing={2}>
            <Text variant="body-medium">
              {intl.formatMessage({
                id: 'DeleteDocumentWithReasonModal.tellUsMore',
                defaultMessage: 'Tell us more',
                description: 'Prompt for additional details',
              })}
            </Text>
            <Text variant="body-medium">
              {deleteReason === 'other'
                ? intl.formatMessage({
                    id: 'DeleteDocumentWithReasonModal.requiredText',
                    defaultMessage: '(Required)',
                    description: 'Required label for other reason',
                  })
                : intl.formatMessage({
                    id: 'DeleteDocumentWithReasonModal.optionalText',
                    defaultMessage: '(Optional)',
                    description: 'Optional label for other reason',
                  })}
            </Text>
            <TextArea
              placeholder={intl.formatMessage({
                id: 'DeleteDocumentWithReasonModal.placeholder',
                defaultMessage: 'Your reason',
                description: 'Placeholder text for delete comment input',
              })}
              value={deleteComment}
              onChange={handleCommentChange}
              name="reason"
            />
          </Layout>
          <Text variant="tiny" color="text.secondary">
            {intl.formatMessage(
              {
                id: 'DeleteDocumentWithReasonModal.characterCount',
                defaultMessage: '{count} / {limit} characters',
                description: 'Character count for delete comment',
              },
              { count: deleteComment.length, limit: CHAR_LIMIT },
            )}
          </Text>
        </div>
      </Layout>
    </Modal>
  );
  if (!isLoaded) {
    content = (
      <Modal title="" isVisible={isVisible} onCancel={null}>
        <ContentContainer
          loadingContent={{
            isLoading: true,
          }}
        />
      </Modal>
    );
  } else if (overLimit) {
    content = (
      <LimitExceededModal
        actionType="delete"
        count={documentCount}
        limit={BULK_DELETE_LIMIT}
        isVisible={isVisible}
        hideModal={handleHide}
      />
    );
  }

  return content;
};

export default DeleteDocumentWithReasonModal;
